import axios from 'axios';
import { supabase } from '@/lib/supabaseClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach JWT token and session ID to every request automatically.
// Impersonation tabs store their token in sessionStorage (tab-isolated) — prefer it over localStorage.
api.interceptors.request.use((config) => {
  const isImpersonation = sessionStorage.getItem('kiranaflow_impersonation') === 'true';
  const token = isImpersonation
    ? sessionStorage.getItem('kiranaflow_token')
    : localStorage.getItem('kiranaflow_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // X-Session-ID not needed on impersonation tabs — backend skips the check for is_impersonation JWTs
  if (!isImpersonation) {
    const sessionId = localStorage.getItem('kiranaflow_session_id');
    if (sessionId) config.headers['X-Session-ID'] = sessionId;
  }
  return config;
});

// Queue of waiting requests while a token refresh is in flight.
let isRefreshing = false;
let refreshQueue = [];

const processQueue = (error, token = null) => {
  refreshQueue.forEach(p => error ? p.reject(error) : p.resolve(token));
  refreshQueue = [];
};

export const clearAuth = () => {
  if (sessionStorage.getItem('kiranaflow_impersonation') === 'true') {
    // Impersonation tab — clear sessionStorage only; never touch the admin's localStorage
    sessionStorage.clear();
  } else {
    localStorage.removeItem('kiranaflow_token');
    localStorage.removeItem('kiranaflow_refresh_token');
    localStorage.removeItem('kiranaflow_user');
    localStorage.removeItem('kiranaflow_session_id');
  }
};

// Redirect to login on auth failures. Handles:
//   401 — session invalid / token expired (attempts silent refresh first)
//   403 — account suspended or subscription expired (detected via detail message)
// Other 403s (e.g. in-app permission denials) are left to the caller.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err.response?.status;

    // Session revoked (another device logged in) — skip refresh, go straight to login.
    if (status === 401 && err.response?.data?.detail === 'session_revoked') {
      clearAuth();
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login?reason=session_revoked';
      }
      return Promise.reject(err);
    }

    // Impersonation token expired — clear sessionStorage only, never touch admin's localStorage.
    if (status === 401 && sessionStorage.getItem('kiranaflow_impersonation') === 'true') {
      clearAuth();
      window.location.href = '/login?reason=session_expired';
      return Promise.reject(err);
    }

    // Attempt silent token refresh on 401 before logging out.
    if (status === 401 && !err.config._retry) {
      const refreshToken = localStorage.getItem('kiranaflow_refresh_token');
      if (refreshToken) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            refreshQueue.push({ resolve, reject });
          }).then(token => {
            err.config.headers.Authorization = `Bearer ${token}`;
            return api(err.config);
          }).catch(e => Promise.reject(e));
        }

        err.config._retry = true;
        isRefreshing = true;

        // Ask supabase-js for the current session instead of calling refreshSession()
        // ourselves. supabase-js serialises every refresh (its own background
        // autoRefreshToken timer included) behind one internal lock, so routing
        // through getSession() guarantees there's only ever one refresh in flight
        // for a given refresh token. Calling refreshSession() directly here used to
        // race the background timer for the same one-time-use refresh token after
        // idle periods — whichever call lost got an "already used" error and forced
        // a spurious logout even though the session was actually fine.
        return supabase.auth.getSession()
          .then(({ data, error: refreshError }) => {
            if (refreshError || !data.session) throw refreshError || new Error('refresh failed');
            const newToken = data.session.access_token;
            localStorage.setItem('kiranaflow_token', newToken);
            localStorage.setItem('kiranaflow_refresh_token', data.session.refresh_token);
            api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
            processQueue(null, newToken);
            err.config.headers.Authorization = `Bearer ${newToken}`;
            return api(err.config);
          })
          .catch(e => {
            processQueue(e, null);
            clearAuth();
            window.location.href = '/login';
            return Promise.reject(e);
          })
          .finally(() => { isRefreshing = false; });
      }
    }

    if (!window.location.pathname.includes('/login')) {
      const detail = err.response?.data?.detail || '';
      const lowerDetail = detail.toLowerCase();
      const isSuspended  = status === 403 && lowerDetail.includes('suspended');
      const isExpired    = status === 403 && lowerDetail.includes('expired');
      const isMFANeeded  = status === 403 && lowerDetail === 'mfa_required';
      const isAuthFailure = status === 401 || isSuspended || isExpired;

      // Safety net: if a request reaches the backend with an aal1 token, redirect to verify
      if (isMFANeeded && !window.location.pathname.startsWith('/mfa/')) {
        window.location.href = '/mfa/verify';
        return Promise.reject(err);
      }

      if (isAuthFailure) {
        clearAuth();
        const reason = isSuspended ? 'suspended' : isExpired ? 'expired' : null;
        window.location.href = reason ? `/login?reason=${reason}` : '/login';
      }
    }
    return Promise.reject(err);
  }
);

export const authAPI = {
  register:       (data)  => api.post('/auth/register', data),
  login:          (data)  => api.post('/auth/login', data),
  googleCallback: (token) => api.post('/auth/google-callback', { access_token: token }),
  forgotPassword: (data)  => api.post('/auth/forgot-password', data),
};

export const storeProfileAPI = {
  get:                ()     => api.get('/store/profile'),
  update:             (data) => api.put('/store/profile', data),
  getStats:           ()     => api.get('/store/stats'),
  changePassword:     (data) => api.post('/store/change-password', data),
  restoreArchive:     ()     => api.post('/stores/me/restore-archive'),
  updateLabelTemplate:(data) => api.patch('/store/label-template', data),
};

// productsAPI kept for backward-compat reads; write methods removed (catalog auto-built from imports)
export const productsAPI = {
  getAll: (params) => api.get('/products', { params }),
  getOne: (id) => api.get(`/products/${id}`),
};

export const catalogAPI = {
  getAll: (params) => api.get('/catalog', { params }),
  getOne: (catalogId) => api.get(`/catalog/${catalogId}`),
  create: (data) => api.post('/catalog', data),
  update: (catalogId, data) => api.patch(`/catalog/${catalogId}`, data),
  delete: (catalogId) => api.delete(`/catalog/${catalogId}`),
  getDuplicateBarcodes: () => api.get('/catalog/duplicate-barcodes'),
  mergeInto: (srcId, tgtId) => api.post(`/catalog/${srcId}/merge-into/${tgtId}`),
  suggestLabelBarcode: (after) => api.get('/catalog/suggest-label-barcode', after != null ? { params: { after } } : {}),
  getUnused: (days = 90, limit = 200, skip = 0) => api.get('/catalog/unused', { params: { days, limit, skip } }),
};

export const stockAPI = {
  getAll: (params) => api.get('/stock-live', { params }),
  getOne: (catalogId) => api.get(`/stock-live/${catalogId}`),
  setStock: (catalogId, stock) => api.patch(`/stock-levels/${catalogId}`, { stock }),
};

export const debugAPI = {
  getStockHealth: () => api.get('/debug/stock-health'),
};

export const customersAPI = {
  getAll: (params) => api.get('/customers', { params }),
  get: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.patch(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
};

export const khataAPI = {
  collect: (data) => api.post('/khata/collect', data),
  getPayments: (params) => api.get('/khata/payments', { params }),
  deletePayment: (id) => api.delete(`/khata/transactions/${id}`),
  updatePayment: (id, data) => api.patch(`/khata/transactions/${id}`, data),
};

export const posAPI = {
  getProducts:    (params)      => api.get('/pos/products', { params }),
  getProductsVersion: ()        => api.get('/pos/products/version'),
  listCustomers:  (params)      => api.get('/customers', { params }),
  createCustomer: (data)        => api.post('/customers', data),
  createSale:     (data)        => api.post('/pos/sales', data),
  listSales:      (params)      => api.get('/pos/sales', { params }),
  getSale:        (billNumber)  => api.get(`/pos/sales/${encodeURIComponent(billNumber)}`),
  deleteSale:     (billNumber)  => api.delete(`/pos/sales/${encodeURIComponent(billNumber)}`),
  restoreSale:    (billNumber)  => api.post(`/pos/sales/${encodeURIComponent(billNumber)}/restore`),
  parseOrder:     (fd)          => api.post('/pos/parse-order', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

export const paymentAPI = {
  createRazorpayOrder: (data) => api.post('/payment/razorpay/create-order', data),
  verifyRazorpayPayment: (data) => api.post('/payment/razorpay/verify', data),
};

export const salesAPI = {
  getAll: (params) => api.get('/sales', { params }),
  getOne: (id) => api.get(`/sales/${id}`),
  create: (data) => api.post('/sales', data),
  update: (id, data) => api.put(`/sales/${id}`, data),
  delete: (id) => api.delete(`/sales/${id}`),
  restore: (id) => api.post(`/sales/${id}/restore`),
};

export const whatsappAPI = {
  sendReceipt: (data) => api.post('/whatsapp/send-receipt', data),
};

export const suppliersAPI = {
  getAll: (params) => api.get('/suppliers', { params }),
  getOne: (id) => api.get(`/suppliers/${id}`),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.patch(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
};

export const dashboardAPI = {
  getSummary: () => api.get('/dashboard/summary'),
};

export const reportsAPI = {
  getSalesReport:   (startDate, endDate, catalogId) => api.get('/reports/sales',      { params: { start_date: startDate, end_date: endDate, catalog_id: catalogId || undefined } }),
  getCategories:    (startDate, endDate, catalogId) => api.get('/reports/categories', { params: { start_date: startDate, end_date: endDate, catalog_id: catalogId || undefined } }),
  getProfitLoss:    (startDate, endDate) => api.get('/reports/profit-loss',{ params: { start_date: startDate, end_date: endDate } }),
  getDayBook:       (date)               => api.get('/reports/day-book',   { params: { date } }),
  getGstReturns:    (month)              => api.get('/reports/gst-returns',          { params: { month } }),
  getGstInvoices:   (month)              => api.get('/reports/gst-returns/invoices', { params: { month } }),
  getExpenses:      (startDate, endDate) => api.get('/reports/expenses',   { params: { start_date: startDate, end_date: endDate } }),
  addExpense:       (data)               => api.post('/reports/expenses', data),
  deleteExpense:    (id)                 => api.delete(`/reports/expenses/${id}`),
  getJournalLedger: (startDate, endDate, account) => api.get('/reports/journal-ledger', { params: { start_date: startDate, end_date: endDate, account } }),
  getBalanceSheet:         (asOf)              => api.get('/reports/balance-sheet',            { params: { as_of: asOf } }),
  getHourlyHeatmap:        (startDate, endDate, catalogId) => api.get('/reports/sales/hourly-heatmap',        { params: { start_date: startDate, end_date: endDate, catalog_id: catalogId || undefined } }),
  getBillSizeDistribution: (startDate, endDate, catalogId) => api.get('/reports/sales/bill-size-distribution', { params: { start_date: startDate, end_date: endDate, catalog_id: catalogId || undefined } }),
  getProductPairs:         (startDate, endDate, catalogId) => api.get('/reports/sales/product-pairs',          { params: { start_date: startDate, end_date: endDate, catalog_id: catalogId || undefined } }),
  getSeasonality:          (months = 12, catalogId)        => api.get('/reports/seasonality',                    { params: { months, catalog_id: catalogId || undefined } }),
};

export const inventoryAPI = {
  getExpiringProducts: (days) => api.get(`/products/expiring?days=${days}`),
};

export const analyticsAPI = {
  getTopSelling:       (limit = 10, days = 30) => api.get('/analytics/top-selling', { params: { limit, days } }),
  getDeadStock:        (days_threshold)         => api.get('/analytics/dead-stock',  days_threshold != null ? { params: { days_threshold } } : {}),
  getMarginLeakage:    (days = 90)            => api.get('/analytics/margin-leakage', { params: { days } }),
  getCustomerInsights: ()                       => api.get('/analytics/customers'),
  getWac:              ()                       => api.get('/analytics/wac'),
};

export const reorderAPI = {
  getSuggestions: () => api.get('/analytics/reorder-suggestions'),
};


export const purchasesAPI = {
  getAll:          (params) => api.get('/purchases', { params }),
  create:          (data)   => api.post('/purchases', data),
  resolveItem:     (data)   => api.post('/purchases/resolve-item', data),
  lookupBarcode:   (bc)     => api.get(`/purchases/barcode/${encodeURIComponent(bc)}`),
  suggestBarcode:  (after)  => api.get('/purchases/barcode/suggest', after != null ? { params: { after } } : {}),
  parseInvoice:    (fd)     => api.post('/purchases/parse-invoice', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deletePurchase:  (id)     => api.delete(`/purchases/${id}`),
  restorePurchase: (id)     => api.post(`/purchases/${id}/restore`),
  updatePurchase:  (id, data) => api.patch(`/purchases/${id}`, data),
};

export const incomingInvoicesAPI = {
  getCount:    ()          => api.get('/purchases/incoming/count'),
  getAll:      (params)    => api.get('/purchases/incoming', { params }),
  getOne:      (id)        => api.get(`/purchases/incoming/${id}`),
  acknowledge: (id, data)  => api.post(`/purchases/incoming/${id}/acknowledge`, data),
  dispute:     (id, data)  => api.post(`/purchases/incoming/${id}/dispute`, data),
  reject:      (id, data)  => api.post(`/purchases/incoming/${id}/reject`, data),
};

export const supplierInvoicesAPI = {
  getAll:   (params)    => api.get('/supplier/invoices', { params }),
  getOne:   (id)        => api.get(`/supplier/invoices/${id}`),
  revise:   (id, data)  => api.patch(`/supplier/invoices/${id}`, data),
  cancel:   (id)        => api.delete(`/supplier/invoices/${id}`),
  respond:  (id, data)  => api.post(`/supplier/invoices/${id}/respond`, data),
  restore:  (id)        => api.post(`/supplier/invoices/${id}/restore`),
};

export const aiAPI = {
  getInsights: () => api.get('/ai/insights'),
};

export const valueSummaryAPI = {
  get: () => api.get('/value-summary'),
};

export const smartActionsAPI = {
  getAll:     (params) => api.get('/smart-actions', { params }),
  address:    (id, notes) => api.post(`/smart-actions/${id}/address`, { notes }),
  dismiss:    (id) => api.post(`/smart-actions/${id}/dismiss`),
  reopen:     (id) => api.post(`/smart-actions/${id}/reopen`),
  regenerate: () => api.post('/smart-actions/regenerate'),
};


export const adminAPI = {
  getStats:           ()                       => api.get('/admin/stats'),
  getCustomers:       ()                       => api.get('/admin/customers'),
  updateCustomer:     (id, data)               => api.put(`/admin/customers/${id}`, data),
  createCustomer:     (data)                   => api.post('/admin/customers', data),
  deleteCustomer:     (id)                     => api.delete(`/admin/customers/${id}`),
  hardDeleteCustomer: (id, confirmation)       => api.delete(`/admin/customers/${id}/hard`, { data: { confirmation } }),
  restoreCustomer:    (id)                     => api.post(`/admin/customers/${id}/restore`),
  resetPassword:      (id, password)           => api.post(`/admin/customers/${id}/reset-password`, { password }),
};

export const adminDebugAPI = {
  // Collection viewer
  getData:        (ownerId, collection, params) =>
    api.get(`/admin/customers/${ownerId}/data`, { params: { collection, ...params } }),
  updateDoc:      (ownerId, collection, docId, data) =>
    api.put(`/admin/customers/${ownerId}/data/${collection}/${docId}`, data),
  deleteDoc:      (ownerId, collection, docId) =>
    api.delete(`/admin/customers/${ownerId}/data/${collection}/${docId}`),
  deleteAllDocs:  (ownerId, collection) =>
    api.delete(`/admin/customers/${ownerId}/data/${collection}/all`),
  createDoc:      (ownerId, collection, data) =>
    api.post(`/admin/customers/${ownerId}/data/${collection}`, data),
  // Upload history
  getUploads:   (ownerId)        => api.get(`/admin/customers/${ownerId}/uploads`),
  deleteUpload: (ownerId, logId) => api.delete(`/admin/customers/${ownerId}/uploads/${logId}`),
};

export const adminCatalogAPI = {
  validateStockMaster:  (ownerId, fd) => api.post(
    `/admin/customers/${ownerId}/stock-master/validate`, fd,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  ),
  confirmStockMaster:   (ownerId, body) => api.post(`/admin/customers/${ownerId}/stock-master/confirm`, body),
  saveStockMaster:      (ownerId, body) => api.post(`/admin/customers/${ownerId}/stock-master/save`, body),
  validateOpeningStock: (ownerId, fd) => api.post(
    `/admin/customers/${ownerId}/opening-stock/validate`, fd,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  ),
  confirmOpeningStock:  (ownerId, body) => api.post(`/admin/customers/${ownerId}/opening-stock/confirm`, body),
  saveOpeningStock:     (ownerId, body) => api.post(`/admin/customers/${ownerId}/opening-stock/save`, body),
  validateCombinedUpload: (ownerId, fd) => api.post(
    `/admin/customers/${ownerId}/combined-upload/validate`, fd,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  ),
  confirmCombinedUpload: (ownerId, body, onUploadProgress) => api.post(`/admin/customers/${ownerId}/combined-upload/confirm`, body, { onUploadProgress }),
  saveCombinedUpload:    (ownerId, body, onUploadProgress) => api.post(`/admin/customers/${ownerId}/combined-upload/save`, body, { onUploadProgress }),
  lookupBarcode:        (ownerId, barcode) => api.get(`/admin/customers/${ownerId}/catalog/barcode/${encodeURIComponent(barcode)}`),
  finalizeStockMaster:  (ownerId) => api.post(`/admin/customers/${ownerId}/stock-master/finalize`),
  getCatalog:           (ownerId, params) => api.get(`/admin/customers/${ownerId}/catalog`, { params }),
  getCatalogEntry:      (ownerId, catalogId) => api.get(`/admin/customers/${ownerId}/catalog/${encodeURIComponent(catalogId)}`),
  updateCatalogEntry:   (ownerId, catalogId, body) => api.put(`/admin/customers/${ownerId}/catalog/${encodeURIComponent(catalogId)}`, body),
  deleteCatalogEntry:   (ownerId, catalogId) => api.delete(`/admin/customers/${ownerId}/catalog/${encodeURIComponent(catalogId)}`),
  getStockLevels:       (ownerId, params) => api.get(`/admin/customers/${ownerId}/stock-levels`, { params }),
  updateStockLevel:     (ownerId, catalogId, stock) => api.put(`/admin/customers/${ownerId}/stock-levels/${encodeURIComponent(catalogId)}`, { stock }),
  deleteStockLevel:     (ownerId, catalogId) => api.delete(`/admin/customers/${ownerId}/stock-levels/${encodeURIComponent(catalogId)}`),
  validateImportCustomers: (ownerId, fd) => api.post(
    `/admin/customers/${ownerId}/import-customers/validate`, fd,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  ),
  importCustomers: (ownerId, fd, onUploadProgress) => api.post(
    `/admin/customers/${ownerId}/import-customers`, fd,
    { headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress }
  ),
};

export const adminSubUsersAPI = {
  list:   (ownerId)              => api.get(`/admin/customers/${ownerId}/sub-users`),
  create: (ownerId, data)        => api.post(`/admin/customers/${ownerId}/sub-users`, data),
  update: (ownerId, subId, data) => api.patch(`/admin/customers/${ownerId}/sub-users/${subId}`, data),
  remove: (ownerId, subId)       => api.delete(`/admin/customers/${ownerId}/sub-users/${subId}`),
  setMFAExempt: (userId, mfa_exempt) => api.patch(`/admin/customers/${userId}/mfa-exempt`, { mfa_exempt }),
};

export const ownerSubUsersAPI = {
  list:   ()            => api.get('/store/sub-users'),
  update: (subId, data) => api.patch(`/store/sub-users/${subId}`, data),
};

export const exportAPI = {
  downloadZip: async (startDate, endDate, files) => {
    const token = localStorage.getItem('kiranaflow_token');
    let url = `${BACKEND_URL}/api/store/export/zip?start=${startDate}&end=${endDate}`;
    if (files && files.length > 0) url += `&files=${files.join(',')}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `Export failed (${res.status})`);
    }
    return res.blob();
  },
};

// ── Chain owner portal ────────────────────────────────────────────────────────
export const chainAPI = {
  getStores:        ()          => api.get('/chain/stores'),
  getAnalytics:     (days = 30) => api.get(`/chain/analytics?days=${days}`),
  getProducts:      ()          => api.get('/chain/analytics/products'),
  getInventory:     ()          => api.get('/chain/analytics/inventory'),
  getFinancials:    (days = 30) => api.get(`/chain/analytics/financials?days=${days}`),
  getCustomers:     ()          => api.get('/chain/analytics/customers'),
  getStoreSnapshot:  (storeId)             => api.get(`/chain/store/${storeId}/snapshot`),
  getRevenueTrend:   (startDate, endDate)  => api.get(`/chain/analytics/revenue-trend?start_date=${startDate}&end_date=${endDate}`),
};

// ── Chain store-level proxy API ───────────────────────────────────────────────
export const chainStoreAPI = {
  getCustomerInsights: (sid)      => api.get(`/chain/store/${sid}/customer-insights`),
  getSalesReport:      (sid, p)   => api.get(`/chain/store/${sid}/reports/sales`, { params: p }),
  getHourlyHeatmap:    (sid, p)   => api.get(`/chain/store/${sid}/reports/sales/hourly-heatmap`, { params: p }),
  getBillDistribution: (sid, p)   => api.get(`/chain/store/${sid}/reports/sales/bill-size-distribution`, { params: p }),
  getProductPairs:     (sid, p)   => api.get(`/chain/store/${sid}/reports/sales/product-pairs`, { params: p }),
  getCategories:       (sid, p)   => api.get(`/chain/store/${sid}/reports/categories`, { params: p }),
  getSeasonality:      (sid, p)   => api.get(`/chain/store/${sid}/reports/seasonality`, { params: p }),
  getProfitLoss:       (sid, p)   => api.get(`/chain/store/${sid}/reports/profit-loss`, { params: p }),
  getDayBook:          (sid, p)   => api.get(`/chain/store/${sid}/reports/day-book`, { params: p }),
  getSalesRegister:    (sid, p)   => api.get(`/chain/store/${sid}/sales`, { params: p }),
  getPurchaseRegister: (sid, p)   => api.get(`/chain/store/${sid}/purchases`, { params: p }),
  getGSTReturns:       (sid, p)   => api.get(`/chain/store/${sid}/reports/gst-returns`, { params: p }),
  getGSTInvoices:      (sid, p)   => api.get(`/chain/store/${sid}/reports/gst-returns/invoices`, { params: p }),
  getExpenses:         (sid, p)   => api.get(`/chain/store/${sid}/reports/expenses`, { params: p }),
  getJournalLedger:    (sid, p)   => api.get(`/chain/store/${sid}/reports/journal-ledger`, { params: p }),
  getBalanceSheet:     (sid, p)   => api.get(`/chain/store/${sid}/reports/balance-sheet`, { params: p }),
};

// ── Supplier portal ───────────────────────────────────────────────────────────
export const supplierAPI = {
  getStores:          ()              => api.get('/supplier/stores'),
  getStock:           (storeId)       => api.get(`/supplier/store/${storeId}/stock`),
  getFullStock:       (storeId)       => api.get(`/supplier/store/${storeId}/stock-full`),
  getPurchases:       (storeId)       => api.get(`/supplier/store/${storeId}/purchases`),
  getOverview:        ()              => api.get('/supplier/overview'),
  getInvoices:        (params)        => api.get('/supplier/invoices', { params }),
  getInvoice:         (id)            => api.get(`/supplier/invoices/${id}`),
  getCredit:          ()              => api.get('/supplier/credit'),
  recordCollection:   (storeId, data) => api.post(`/supplier/credit/${storeId}/collect`, data),
  getCollectionHistory: (storeId)     => api.get(`/supplier/credit/${storeId}/history`),
  deleteCollection:   (collectionId)  => api.delete(`/supplier/credit/collections/${collectionId}`),
  editCollection:     (collectionId, data) => api.patch(`/supplier/credit/collections/${collectionId}`, data),
};

// ── Store owner supplier payables ─────────────────────────────────────────────
export const payablesAPI = {
  getSuppliers:  ()                => api.get('/payables/suppliers'),
  recordPayment: (portalId, data)  => api.post(`/payables/suppliers/${portalId}/pay`, data),
  getHistory:    (portalId)        => api.get(`/payables/suppliers/${portalId}/history`),
  deletePayment: (paymentId)       => api.delete(`/payables/payments/${paymentId}`),
  editPayment:   (paymentId, data) => api.patch(`/payables/payments/${paymentId}`, data),
};

// ── Admin portal management ───────────────────────────────────────────────────
export const adminChainPortalsAPI = {
  list:        ()                              => api.get('/admin/chain-portals'),
  create:      (data)                          => api.post('/admin/chain-portals', data),
  addStore:    (chainId, storeId)              => api.post(`/admin/chain-portals/${chainId}/stores`, { store_id: storeId }),
  removeStore: (chainId, storeId)              => api.delete(`/admin/chain-portals/${chainId}/stores/${storeId}`),
  hardDelete:  (chainId, confirmation)         => api.delete(`/admin/chain-portals/${chainId}/hard`, { data: { confirmation } }),
};

export const adminSupplierPortalsAPI = {
  list:        ()                                    => api.get('/admin/supplier-portals'),
  create:      (data)                                => api.post('/admin/supplier-portals', data),
  linkStore:   (portalId, storeId, localSupplierId)  => api.post(`/admin/supplier-portals/${portalId}/stores`, { store_id: storeId, local_supplier_id: localSupplierId }),
  unlinkStore: (portalId, storeId)                   => api.delete(`/admin/supplier-portals/${portalId}/stores/${storeId}`),
  hardDelete:  (portalId, confirmation)              => api.delete(`/admin/supplier-portals/${portalId}/hard`, { data: { confirmation } }),
};

export const adminStoreSupplierAPI = {
  list: (ownerId) => api.get(`/admin/customers/${ownerId}/suppliers`),
};

export const connectionsAPI = {
  getAll:             ()                  => api.get('/admin/connections'),
  connect:            (portalId, storeId) => api.post('/admin/connections', { supplier_portal_id: portalId, store_id: storeId }),
  disconnect:         (portalId, storeId) => api.delete(`/admin/connections/${portalId}/${storeId}`),
  allSupplierDetails: ()                  => api.get('/admin/all-supplier-details'),
};

export const getHomeRoute = (role) => {
  if (role === 'platform_admin') return '/admin';
  if (role === 'chain_owner')    return '/chain/dashboard';
  if (role === 'supplier')       return '/supplier/overview';
  return '/pos';
};

export default api;
